import Link from 'next/link';

import {
  CompetitionLogo,
  competitionLogoFragment,
} from '@/components/CompetitionLogo';
import { Medal } from '@/components/Medal';
import { getMatchdayUrl, getSeasonUrl } from '@/utils';
import { graphql, readFragment, type FragmentOf } from '@/utils/graphql';

export const profileMedalCardFragment = graphql(
  `
    fragment profileMedalCardFields on positions {
      position
      matchday {
        slug
        name
        season {
          slug
          name
          competition {
            slug
            name
            ...competitionLogoFields
            parent {
              name
            }
          }
        }
      }
      season {
        slug
        name
        competition {
          slug
          name
          ...competitionLogoFields
          parent {
            name
          }
        }
      }
    }
  `,
  [competitionLogoFragment],
);

export function ProfileMedalCard({
  position: _position,
}: {
  position: FragmentOf<typeof profileMedalCardFragment>;
}) {
  const position = readFragment(profileMedalCardFragment, _position);

  let competition;
  let title;
  let subTitle;
  let href;

  if (position.season) {
    const season = position.season;
    const parentName = season.competition!.parent?.name;
    const competitionName = season.competition!.name;
    competition = season.competition!;
    title = parentName ? `${parentName} ${competitionName}` : competitionName;
    subTitle = `Temporada ${season.name}`;
    href = getSeasonUrl(season.competition!, season);
  } else {
    const matchday = position.matchday!;
    const parentName = matchday.season!.competition!.parent?.name;
    const competitionName = matchday.season!.competition!.name;
    competition = matchday.season!.competition!;
    title = parentName ? `${parentName} ${competitionName}` : competitionName;
    subTitle = `Temporada ${matchday.season!.name} - ${matchday.name}`;
    href = getMatchdayUrl(
      matchday.season!.competition!,
      matchday.season!,
      matchday,
    );
  }

  return (
    <div className="relative flex items-center space-x-3 px-3 py-2 border rounded-md hover:bg-accent">
      <CompetitionLogo competition={competition} size={48} />

      <div className="flex-1 min-w-0">
        <Link
          href={href}
          className="after:absolute after:inset-0"
          prefetch={false}
        >
          <div className="text-sm font-medium truncate">{title}</div>
          <div className="text-muted-foreground text-xs truncate">
            {subTitle}
          </div>
        </Link>
      </div>

      <Medal position={position.position} />
    </div>
  );
}
